/**
 * Every figure at once (spec 05 §9) on level 1's stage: Bub and Bob at their starts, each
 * monster calm on the lowest shelf and angry on the one above, Skel-monsta overhead. The
 * figure review page: judge them here, in the real pipeline, before they meet the rules.
 *
 *   ?scene=lineup&level=1&t=0.3
 */

import { Scene, Vector3 } from 'three';
import stageGrade from '../../content/grades/stage.json';
import { LEVELS, level } from '../levels';
import { gridFromRows } from '../levels/grid';
import type { MonsterKind } from '../levels/types';
import { buildDragon } from '../render/actors/dragon';
import { buildMonster, buildSkelMonsta, face } from '../render/actors/monsters';
import { StageCamera } from '../render/camera/StageCamera';
import { levelLook } from '../render/levelLook';
import { createGradientMap, createToonMaterial, OUTLINE } from '../render/materials/toon';
import { type Grade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { tileOrigin } from '../render/stage/blocks';
import { createStageLights, StageView } from '../render/stage/StageView';
import { chooseSize, createRenderer, exposeLook, num } from './devKit';
import { poseDragon } from './lookdevPoses';

const KINDS: readonly MonsterKind[] = ['zenChan', 'mighta', 'monsta', 'pulpul', 'banebou', 'hidegons', 'invader', 'drunk'];

export function startLineup(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const def = level(Math.round(num(params, 'level', 1)));
  const look = levelLook(def);
  const grade = stageGrade as Grade;
  const palette = new Palette(look, params.get('figures') === 'c64' ? 'c64' : 'arcade');
  const gradientMap = createGradientMap();
  const renderer = createRenderer(canvas);
  const grid = gridFromRows(def.tiles);
  const scene = new Scene();
  scene.add(new StageView({ grid, style: look.style, palette, gradientMap, seed: 1 }).group);
  scene.add(createStageLights(grade, grid.width, grid.height));

  const players = createToonMaterial({ gradientMap, outline: OUTLINE.player, depthBias: 3 });
  const figures = createToonMaterial({ gradientMap, outline: OUTLINE.character, depthBias: 3 });
  const feetOn = (col: number, row: number): Vector3 => {
    const o = tileOrigin(grid, col, row);
    return new Vector3(o.x + 1, o.y + 1, 0);
  };
  const bub = buildDragon(palette, 'bub', players);
  const bob = buildDragon(palette, 'bob', players);
  const { p1, p2 } = LEVELS.playerSpawns;
  bub.root.position.set((p1.x + 12) / 8 - 16, 12.5 - (p1.y + 21) / 8, 0);
  bob.root.position.set((p2.x + 12) / 8 - 16, 12.5 - (p2.y + 21) / 8, 0);
  scene.add(bub.root, bob.root);

  KINDS.forEach((kind, i) => {
    for (const [angry, row] of [[false, 19], [true, 14]] as const) {
      const m = buildMonster(kind, angry, palette, figures);
      m.root.position.copy(feetOn(8 + i * 2, row));
      face(m, i % 2 === 0 ? 1 : -1);
      scene.add(m.root);
    }
  });
  const skel = buildSkelMonsta(palette, figures);
  skel.root.position.copy(feetOn(15, 6));
  face(skel, -1);
  scene.add(skel.root);

  const size = chooseSize(params);
  const camera = new StageCamera(size, num(params, 'ppt', 14) * size.scale, num(params, 'pitch', 18));
  const pipeline = new RenderPipeline(renderer, palette.buildLut(), palette.color('ink.0'), size);
  pipeline.setGrade(grade, look.fog);
  const frozen = params.has('t');
  const start = performance.now();
  const frame = (): void => {
    const t = frozen ? num(params, 't', 0) : (performance.now() - start) / 1000;
    poseDragon(bub, 'idle', t);
    poseDragon(bob, 'idle', t, -1);
    pipeline.render(scene, camera);
    if (!frozen) requestAnimationFrame(frame);
  };
  frame();
  exposeLook(pipeline, frame, { camera });
}
